import { useState } from 'react'
import { IconType } from 'react-icons'
import {
  PiCopy,
  PiPackage,
  PiPlus,
  PiSortAscending,
  PiSortDescending,
  PiTrash,
  PiUser,
  PiUsersThree,
} from 'react-icons/pi'
import { Link } from 'react-router-dom'
import { PillTabs } from 'components/FbUI/PillTabs'
import { Popover, PopoverContent, PopoverTrigger } from 'components/UI/popover'
import { Kpi } from '../kpis'
import { ViewConfig } from '../view-config'
import { FORM_INPUT_CLASS } from './formStyles'
import {
  defaultReportIcon,
  REPORT_ICONS,
  REPORT_TYPE_LABEL,
  reportType,
  ReportType,
} from './reportIcons'

/**
 * One library row — either a saved/shipped report (links to its detail page) or
 * a goal (no detail page; edits inline). The action callbacks mirror the card
 * variants: views duplicate (+ delete when owned), goals save/delete.
 */
/** Where a report came from. `team` (shared org-wide) is reserved — no data yet. */
export type ReportSource = 'prebuilt' | 'mine' | 'team'

export const SOURCE_LABEL: Record<ReportSource, string> = {
  prebuilt: 'Pre-built',
  mine: 'Mine',
  team: 'Team',
}

/** Glyph per source — pre-built = shipped/packaged, mine = the user, team = org. */
export const SOURCE_ICONS: Record<ReportSource, IconType> = {
  prebuilt: PiPackage,
  mine: PiUser,
  team: PiUsersThree,
}

/**
 * Source chip (icon + label) shown wherever a report's origin matters — the
 * library table, the report detail header, a dashboard tile corner. `iconOnly`
 * drops the label (tile corners) and moves it to a tooltip.
 */
export function SourceBadge({
  source,
  iconOnly,
}: {
  source: ReportSource
  iconOnly?: boolean
}) {
  const Icon = SOURCE_ICONS[source]
  return (
    <span
      title={iconOnly ? SOURCE_LABEL[source] : undefined}
      className="inline-flex items-center gap-1 whitespace-nowrap rounded-full bg-surface-hover px-2 py-0.5 text-xs font-medium text-fg-secondary"
    >
      <Icon size={13} />
      {!iconOnly && SOURCE_LABEL[source]}
    </span>
  )
}

export type LibraryRow =
  | {
      kind: 'view'
      source: ReportSource
      view: ViewConfig
      /** Library mode: clone to "Mine". Omitted in picker mode. */
      onDuplicate?: () => void
      /** Present only for the user's own saved views. */
      onDelete?: () => void
      /** Picker mode only: add this report as a dashboard tile. */
      onAdd?: () => void
      /** Library mode: the user's dashboards this report can be added to. */
      dashboards?: { id: string; name: string }[]
      /** Library mode: append this report as a tile on the given dashboard. */
      onAddToDashboard?: (dashboardId: string) => void
      /** Library mode: create a new dashboard seeded with this report. */
      onAddToNewDashboard?: () => void
    }
  | {
      kind: 'goal'
      source: ReportSource
      kpi: Kpi
      onSave: (patch: { name: string; value: number }) => void
      onDelete: () => void
      /** Library mode: clone this goal to a new "(copy)". */
      onDuplicate?: () => void
    }

const rowName = (r: LibraryRow): string =>
  r.kind === 'view' ? r.view.name : r.kpi.name

const rowId = (r: LibraryRow): string =>
  r.kind === 'view' ? r.view.id : r.kpi.id

const rowType = (r: LibraryRow): ReportType =>
  r.kind === 'view' ? reportType(r.view) : 'goal'

const rowDescription = (r: LibraryRow): string | undefined =>
  r.kind === 'view' ? r.view.description : undefined

// Filter pills (only buckets actually present in `rows` are offered).
type TypeFilter = ReportType | 'all'
const TYPE_TABS: { key: TypeFilter; title: string }[] = [
  { key: 'all', title: 'All' },
  { key: 'metric', title: 'Metrics' },
  { key: 'chart', title: 'Charts' },
  { key: 'table', title: 'Tables' },
  { key: 'goal', title: 'Goals' },
]

type SourceFilter = ReportSource | 'all'
const SOURCE_TABS: { key: SourceFilter; title: string }[] = [
  { key: 'all', title: 'All sources' },
  { key: 'prebuilt', title: SOURCE_LABEL.prebuilt },
  { key: 'mine', title: SOURCE_LABEL.mine },
  { key: 'team', title: SOURCE_LABEL.team },
]

/**
 * Metabase-style report library: a Name / Source / Type / Description table that
 * scans far better than a card grid once the list grows. Name is the one sortable
 * column (asc/desc). With `filterable`, it owns the source/type pills + search
 * bar and filters in-table; otherwise it lists `rows` as given.
 *
 * @param rows - the reports/goals to list
 * @param picker - rows add to a dashboard instead of linking to a detail page
 * @param filterable - show the filter bar and filter rows in-table
 */
const PAGE_SIZE = 10

export function LibraryTable({
  rows,
  picker = false,
  filterable = false,
}: {
  rows: LibraryRow[]
  /** Picker mode: rows add to a dashboard (Add action) instead of linking out. */
  picker?: boolean
  /** Show the source/type pills + search bar and filter rows in-table. */
  filterable?: boolean
}) {
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc')
  const [page, setPage] = useState(0)
  const [typeFilter, setTypeFilter] = useState<TypeFilter>('all')
  const [sourceFilter, setSourceFilter] = useState<SourceFilter>('all')
  const [query, setQuery] = useState('')

  // Pills offer only the buckets actually present; fall back to 'all' if the
  // active selection was filtered away. Identical to the library page's logic,
  // so both report tables filter the same way.
  const presentTypes = new Set<TypeFilter>(['all', ...rows.map(rowType)])
  const presentSources = new Set<SourceFilter>([
    'all',
    ...rows.map((r) => r.source),
  ])
  const activeType =
    filterable && presentTypes.has(typeFilter) ? typeFilter : 'all'
  const activeSource =
    filterable && presentSources.has(sourceFilter) ? sourceFilter : 'all'
  const q = query.trim().toLowerCase()
  const filtered = filterable
    ? rows.filter(
        (r) =>
          (activeType === 'all' || rowType(r) === activeType) &&
          (activeSource === 'all' || r.source === activeSource) &&
          (q === '' ||
            rowName(r).toLowerCase().includes(q) ||
            (rowDescription(r) ?? '').toLowerCase().includes(q))
      )
    : rows

  const sorted = [...filtered].sort((a, b) => {
    const cmp = rowName(a).localeCompare(rowName(b))
    return sortDir === 'asc' ? cmp : -cmp
  })

  // Clamp instead of resetting via an effect: when filtering shrinks the list
  // below the current page, fall back to the last valid page on render.
  const pageCount = Math.max(1, Math.ceil(sorted.length / PAGE_SIZE))
  const safePage = Math.min(page, pageCount - 1)
  const start = safePage * PAGE_SIZE
  const pageRows = sorted.slice(start, start + PAGE_SIZE)
  // Reserve the height the unfiltered first page would occupy, padding short
  // pages with spacer rows. Without this, filtering to a small result set (e.g.
  // "Tables" → 1 row) collapses the page's scroll container below the current
  // scrollTop, the browser clamps scroll to 0, and the whole list teleports.
  const fillerCount = Math.max(
    0,
    Math.min(PAGE_SIZE, rows.length) - pageRows.length
  )

  const typeTabs = TYPE_TABS.filter((t) => presentTypes.has(t.key))
  const sourceTabs = SOURCE_TABS.filter((t) => presentSources.has(t.key))

  return (
    <div className="flex flex-col gap-3">
      {filterable && (
        <div className="flex flex-wrap items-center gap-3">
          <PillTabs
            className="w-fit"
            idPrefix="library-source"
            aria-label="Filter reports by source"
            tabs={sourceTabs}
            activeKey={activeSource}
            onChange={setSourceFilter}
          />
          <PillTabs
            className="w-fit"
            idPrefix="library-type"
            aria-label="Filter reports by type"
            tabs={typeTabs}
            activeKey={activeType}
            onChange={setTypeFilter}
          />
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search reports…"
            aria-label="Search reports"
            className="ml-auto w-56 rounded-lg border border-line bg-surface px-3 py-1.5 text-sm text-fg placeholder:text-fg-muted focus:border-brand-active focus:outline-none"
          />
        </div>
      )}
      {filterable && pageRows.length === 0 ? (
        <p className="text-sm text-fg-muted">No reports match.</p>
      ) : (
        <div className="overflow-hidden rounded-lg border border-line">
          <table className="w-full table-fixed text-sm">
            <thead>
              <tr className="border-b border-line bg-surface-hover">
                <th className="w-72 px-4 py-2 text-left">
                  <button
                    type="button"
                    onClick={() =>
                      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'))
                    }
                    className="inline-flex items-center gap-1 text-xs font-semibold uppercase tracking-wide text-fg-secondary hover:text-fg"
                  >
                    Name
                    {sortDir === 'asc' ? (
                      <PiSortAscending size={14} />
                    ) : (
                      <PiSortDescending size={14} />
                    )}
                  </button>
                </th>
                <th className="w-32 px-4 py-2 text-left text-xs font-semibold uppercase tracking-wide text-fg-secondary">
                  Source
                </th>
                <th className="w-40 px-4 py-2 text-left text-xs font-semibold uppercase tracking-wide text-fg-secondary">
                  Type
                </th>
                <th className="px-4 py-2 text-left text-xs font-semibold uppercase tracking-wide text-fg-secondary">
                  Description
                </th>
                <th className="w-32 px-4 py-2" />
              </tr>
            </thead>
            <tbody>
              {pageRows.map((row) => (
                <LibraryTableRow key={rowId(row)} row={row} picker={picker} />
              ))}
              {Array.from({ length: fillerCount }, (_, i) => (
                <tr
                  key={`filler-${i}`}
                  aria-hidden
                  className="border-b border-line last:border-0"
                >
                  <td colSpan={5}>
                    {/* h-14 == a data row's height (49px); block div honors the
                        height where an empty table-cell wouldn't. */}
                    <div className="h-14" />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {pageCount > 1 && (
            <div className="flex items-center justify-between border-t border-line px-4 py-2 text-sm text-fg-secondary">
              <span>
                {start + 1}–{start + pageRows.length} of {sorted.length}
              </span>
              <div className="flex gap-1">
                <button
                  type="button"
                  onClick={() => setPage(safePage - 1)}
                  disabled={safePage === 0}
                  className="rounded-md border border-line px-2.5 py-1 hover:bg-surface-hover disabled:opacity-40 disabled:hover:bg-transparent"
                >
                  Prev
                </button>
                <button
                  type="button"
                  onClick={() => setPage(safePage + 1)}
                  disabled={safePage >= pageCount - 1}
                  className="rounded-md border border-line px-2.5 py-1 hover:bg-surface-hover disabled:opacity-40 disabled:hover:bg-transparent"
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  )
}

/**
 * Small action button in the table's trailing cell. With no `onClick` it renders
 * a greyed, non-interactive placeholder so every row keeps the same icon columns
 * aligned (add / duplicate / delete) even when an action doesn't apply.
 */
function ActionButton({
  label,
  icon: Icon,
  onClick,
}: {
  label: string
  icon: IconType
  onClick?: () => void
}) {
  if (!onClick) {
    return (
      <span aria-hidden className="rounded p-1.5 text-fg-muted opacity-30">
        <Icon size={15} />
      </span>
    )
  }
  return (
    <button
      type="button"
      aria-label={label}
      onClick={onClick}
      className="rounded p-1.5 text-fg-muted transition-colors hover:bg-surface hover:text-fg"
    >
      <Icon size={15} />
    </button>
  )
}

/**
 * Plus-button menu to drop a report onto one of the user's dashboards (or a
 * fresh one). Lists only "Mine" dashboards — shipped ones aren't editable.
 * Selection closes the popover (radix default).
 */
function AddToDashboardMenu({
  name,
  dashboards,
  onAdd,
  onAddNew,
}: {
  name: string
  dashboards: { id: string; name: string }[]
  onAdd: (dashboardId: string) => void
  onAddNew: () => void
}) {
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          aria-label={`Add ${name} to a dashboard`}
          className="rounded p-1.5 text-fg-muted transition-colors hover:bg-surface hover:text-fg"
        >
          <PiPlus size={15} />
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="max-h-72 w-56 overflow-y-auto p-1">
        <div className="px-2 py-1 text-xs font-semibold uppercase tracking-wide text-fg-secondary">
          Add to dashboard
        </div>
        {dashboards.length === 0 ? (
          <p className="px-2 py-1 text-sm text-fg-muted">
            No saved dashboards yet.
          </p>
        ) : (
          dashboards.map((d) => (
            <button
              key={d.id}
              type="button"
              onClick={() => onAdd(d.id)}
              className="flex w-full items-center rounded px-2 py-1.5 text-left text-sm text-fg hover:bg-surface-hover"
            >
              <span className="flex-1 truncate">{d.name}</span>
            </button>
          ))
        )}
        <button
          type="button"
          onClick={onAddNew}
          className="mt-1 flex w-full items-center gap-2 rounded border-t border-line px-2 py-1.5 text-left text-sm text-fg hover:bg-surface-hover"
        >
          <PiPlus size={14} className="shrink-0 text-fg-muted" /> New dashboard
        </button>
      </PopoverContent>
    </Popover>
  )
}

function LibraryTableRow({
  row,
  picker,
}: {
  row: LibraryRow
  picker: boolean
}) {
  const name = rowName(row)
  // Goal edit state (unused for view rows) — mirrors KpiCard's inline editor.
  const [editing, setEditing] = useState(false)
  const [draftName, setDraftName] = useState(name)
  const [draftValue, setDraftValue] = useState(
    row.kind === 'goal' ? String(row.kpi.value) : ''
  )

  if (row.kind === 'goal' && editing) {
    const save = () => {
      const num = Number(draftValue)
      if (!draftName.trim() || draftValue === '' || Number.isNaN(num)) return
      row.onSave({ name: draftName.trim(), value: num })
      setEditing(false)
    }
    return (
      <tr className="border-b border-line bg-surface-hover last:border-0">
        <td colSpan={5} className="px-4 py-2.5">
          <div className="flex flex-wrap items-center gap-2">
            <input
              aria-label="Goal name"
              value={draftName}
              onChange={(e) => setDraftName(e.target.value)}
              className={FORM_INPUT_CLASS}
            />
            <input
              type="number"
              inputMode="numeric"
              aria-label="Goal value"
              value={draftValue}
              onChange={(e) => setDraftValue(e.target.value)}
              className={FORM_INPUT_CLASS}
            />
            <button
              type="button"
              onClick={save}
              className="rounded-md bg-brand px-3 py-1.5 text-sm font-semibold text-fg-inverse hover:bg-brand-active"
            >
              Save
            </button>
            <button
              type="button"
              onClick={() => setEditing(false)}
              className="rounded-md border border-line px-3 py-1.5 text-sm text-fg hover:bg-surface-hover"
            >
              Cancel
            </button>
          </div>
        </td>
      </tr>
    )
  }

  const typeKey = row.kind === 'view' ? reportType(row.view) : ('goal' as const)
  const Icon =
    row.kind === 'view'
      ? REPORT_ICONS[row.view.icon ?? defaultReportIcon(row.view)]
      : REPORT_ICONS.target
  const description =
    row.kind === 'view'
      ? row.view.description
      : `${row.kpi.dir === 'below' ? 'Target ≤ ' : 'Target ≥ '}${row.kpi.value.toLocaleString()}${row.kpi.unit ? ` ${row.kpi.unit}` : ''}`

  return (
    <tr className="group h-12 border-b border-line transition-colors last:border-0 hover:bg-surface-hover">
      <td className="max-w-[18rem] px-4 py-2.5 font-medium" title={name}>
        {picker ? (
          // Picker mode: no detail page to link to — the row's Add action is the point.
          <span className="block truncate text-fg">{name}</span>
        ) : row.kind === 'view' ? (
          <Link
            to={`/reporting/${row.view.id}`}
            className="block truncate text-fg hover:text-brand-active"
          >
            {name}
          </Link>
        ) : (
          // A goal has no detail page — the name opens its inline editor.
          <button
            type="button"
            onClick={() => {
              setDraftName(name)
              setDraftValue(String(row.kpi.value))
              setEditing(true)
            }}
            className="block max-w-full truncate text-left text-fg hover:text-brand-active"
          >
            {name}
          </button>
        )}
      </td>
      <td className="px-4 py-2.5">
        <SourceBadge source={row.source} />
      </td>
      <td className="px-4 py-2.5">
        <span className="inline-flex items-center gap-1.5 text-fg-secondary">
          <Icon size={15} className="text-brand-active" />
          {REPORT_TYPE_LABEL[typeKey]}
        </span>
      </td>
      <td
        className="truncate px-4 py-2.5 text-fg-secondary"
        title={description}
      >
        {description}
      </td>
      <td className="px-4 py-2.5 text-right whitespace-nowrap">
        <div className="inline-flex gap-1">
          {picker && row.kind === 'view' && row.onAdd && (
            <ActionButton
              label={`Add ${name}`}
              icon={PiPlus}
              onClick={row.onAdd}
            />
          )}
          {/* Fixed add / duplicate / delete columns — greyed when N/A so icons
              stay aligned across rows (goals can't go on a dashboard or clone). */}
          {!picker &&
            (row.kind === 'view' && row.onAddToDashboard ? (
              <AddToDashboardMenu
                name={name}
                dashboards={row.dashboards ?? []}
                onAdd={row.onAddToDashboard}
                onAddNew={row.onAddToNewDashboard ?? (() => {})}
              />
            ) : (
              <ActionButton
                label="Add to dashboard (unavailable)"
                icon={PiPlus}
              />
            ))}
          {!picker && (
            <ActionButton
              label={`Duplicate ${name}`}
              icon={PiCopy}
              onClick={row.onDuplicate}
            />
          )}
          {!picker && (
            <ActionButton
              label={`Delete ${name}`}
              icon={PiTrash}
              onClick={row.onDelete}
            />
          )}
        </div>
      </td>
    </tr>
  )
}
